import { api } from 'trpc/react';
import { MAX_PRODUCT_PRICE_CART_COUNT } from './consts';

export const useAddToCart = () => {
    const utils = api.useUtils();

    return api.cart.add.useMutation({
        onMutate: async variables => {
            await Promise.all([
                utils.cart.getItem.cancel({ productPriceId: variables.productPriceId }),
                utils.cart.getAll.cancel(),
                utils.cart.getCount.cancel(),
            ]);

            const prevGetItem = utils.cart.getItem.getData({ productPriceId: variables.productPriceId });
            const prevGetAll = utils.cart.getAll.getData();
            const prevGetCount = utils.cart.getCount.getData();

            const prevCountItem =
                prevGetItem?.count ??
                prevGetAll?.items.find(i => i.productPriceId === variables.productPriceId)?.count ??
                0;

            const nextCountItem = Math.min((prevCountItem ?? 0) + 1, MAX_PRODUCT_PRICE_CART_COUNT);
            utils.cart.getItem.setData(
                { productPriceId: variables.productPriceId },
                { inCart: true as const, count: nextCountItem, maxCount: MAX_PRODUCT_PRICE_CART_COUNT },
            );

            if (prevGetAll) {
                const exists = prevGetAll.items.some(i => i.productPriceId === variables.productPriceId);
                if (exists) {
                    utils.cart.getAll.setData(undefined, {
                        ...prevGetAll,
                        items: prevGetAll.items.map(i =>
                            i.productPriceId === variables.productPriceId
                                ? { ...i, count: Math.min(i.count + 1, MAX_PRODUCT_PRICE_CART_COUNT) }
                                : i,
                        ),
                    });
                }
            }

            const incrementedCount = prevCountItem === 0;
            if (incrementedCount) {
                utils.cart.getCount.setData(undefined, { count: (prevGetCount?.count ?? 0) + 1 });
            }

            return { prevGetItem, prevGetAll, prevGetCount, incrementedCount };
        },

        onError: (_err, variables, ctx) => {
            if (!ctx) return;
            utils.cart.getItem.setData({ productPriceId: variables.productPriceId }, ctx.prevGetItem);
            if (ctx.prevGetAll) utils.cart.getAll.setData(undefined, ctx.prevGetAll);
            if (ctx.incrementedCount) {
                utils.cart.getCount.setData(undefined, ctx.prevGetCount ?? { count: 0 });
            }
        },

        onSuccess: (res, variables) => {
            utils.cart.getItem.setData(
                { productPriceId: variables.productPriceId },
                { inCart: true as const, count: res.count, maxCount: MAX_PRODUCT_PRICE_CART_COUNT },
            );

            const currentAll = utils.cart.getAll.getData();
            if (currentAll?.items.some(i => i.productPriceId === variables.productPriceId)) {
                utils.cart.getAll.setData(undefined, {
                    ...currentAll,
                    items: currentAll.items.map(i =>
                        i.productPriceId === variables.productPriceId ? { ...i, count: res.count } : i,
                    ),
                });
            }
        },

        onSettled: async (_data, _err, variables) => {
            await Promise.all([
                utils.cart.getItem.invalidate({ productPriceId: variables.productPriceId }),
                utils.cart.getAll.invalidate(),
                utils.cart.getCount.invalidate(),
            ]);
        },
    });
};

export const useRemoveFromCart = () => {
    const utils = api.useUtils();

    return api.cart.remove.useMutation({
        onMutate: async variables => {
            await Promise.all([
                utils.cart.getItem.cancel({ productPriceId: variables.productPriceId }),
                utils.cart.getAll.cancel(),
                utils.cart.getCount.cancel(),
            ]);

            const prevGetItem = utils.cart.getItem.getData({ productPriceId: variables.productPriceId });
            const prevGetAll = utils.cart.getAll.getData();
            const prevGetCount = utils.cart.getCount.getData();

            const prevCountItem =
                prevGetItem?.count ??
                prevGetAll?.items.find(i => i.productPriceId === variables.productPriceId)?.count ??
                0;

            const nextCountItem = Math.max((prevCountItem ?? 0) - 1, 0);

            utils.cart.getItem.setData(
                { productPriceId: variables.productPriceId },
                nextCountItem > 0
                    ? { inCart: true as const, count: nextCountItem, maxCount: MAX_PRODUCT_PRICE_CART_COUNT }
                    : { inCart: false as const, count: 0 as const, maxCount: MAX_PRODUCT_PRICE_CART_COUNT },
            );

            if (prevGetAll) {
                const target = prevGetAll.items.find(i => i.productPriceId === variables.productPriceId);
                if (target) {
                    const dec = Math.max(target.count - 1, 0);
                    utils.cart.getAll.setData(
                        undefined,
                        dec > 0
                            ? {
                                  ...prevGetAll,
                                  items: prevGetAll.items.map(i =>
                                      i.productPriceId === variables.productPriceId ? { ...i, count: dec } : i,
                                  ),
                              }
                            : {
                                  ...prevGetAll,
                                  items: prevGetAll.items.filter(i => i.productPriceId !== variables.productPriceId),
                              },
                    );
                }
            }

            const decrementedCount = prevCountItem === 1;
            if (decrementedCount) {
                utils.cart.getCount.setData(undefined, { count: Math.max((prevGetCount?.count ?? 0) - 1, 0) });
            }

            return { prevGetItem, prevGetAll, prevGetCount, decrementedCount };
        },

        onError: (_err, variables, ctx) => {
            if (!ctx) return;
            utils.cart.getItem.setData({ productPriceId: variables.productPriceId }, ctx.prevGetItem);
            if (ctx.prevGetAll) utils.cart.getAll.setData(undefined, ctx.prevGetAll);
            if (ctx.decrementedCount) {
                utils.cart.getCount.setData(undefined, ctx.prevGetCount ?? { count: 0 });
            }
        },

        onSuccess: (res, variables) => {
            if (res.kind === 'removed') {
                utils.cart.getItem.setData(
                    { productPriceId: variables.productPriceId },
                    { inCart: false as const, count: 0 as const, maxCount: MAX_PRODUCT_PRICE_CART_COUNT },
                );

                const currentAll = utils.cart.getAll.getData();
                if (currentAll) {
                    utils.cart.getAll.setData(undefined, {
                        ...currentAll,
                        items: currentAll.items.filter(i => i.productPriceId !== variables.productPriceId),
                    });
                }
            } else {
                utils.cart.getItem.setData(
                    { productPriceId: variables.productPriceId },
                    { inCart: true as const, count: res.count, maxCount: MAX_PRODUCT_PRICE_CART_COUNT },
                );

                const currentAll = utils.cart.getAll.getData();
                if (currentAll?.items.some(i => i.productPriceId === variables.productPriceId)) {
                    utils.cart.getAll.setData(undefined, {
                        ...currentAll,
                        items: currentAll.items.map(i =>
                            i.productPriceId === variables.productPriceId ? { ...i, count: res.count } : i,
                        ),
                    });
                }
            }
        },

        onSettled: async (_data, _err, variables) => {
            await Promise.all([
                utils.cart.getItem.invalidate({ productPriceId: variables.productPriceId }),
                utils.cart.getAll.invalidate(),
                utils.cart.getCount.invalidate(),
            ]);
        },
    });
};

export const useRemoveAllFromCart = () => {
    const utils = api.useUtils();

    return api.cart.removeAll.useMutation({
        onMutate: async ({ productPriceId }) => {
            await Promise.all([
                utils.cart.getItem.cancel({ productPriceId }),
                utils.cart.getAll.cancel(),
                utils.cart.getCount.cancel(),
            ]);

            const prevGetItem = utils.cart.getItem.getData({ productPriceId });
            const prevGetAll = utils.cart.getAll.getData();
            const prevGetCount = utils.cart.getCount.getData();

            const existedBefore =
                (prevGetItem?.inCart ?? false) ||
                (prevGetAll?.items.some(i => i.productPriceId === productPriceId) ?? false);

            utils.cart.getItem.setData(
                { productPriceId },
                { inCart: false as const, count: 0, maxCount: MAX_PRODUCT_PRICE_CART_COUNT },
            );

            if (prevGetAll) {
                utils.cart.getAll.setData(undefined, {
                    ...prevGetAll,
                    items: prevGetAll.items.filter(i => i.productPriceId !== productPriceId),
                });
            }

            const decrementedCount = existedBefore;
            if (decrementedCount) {
                utils.cart.getCount.setData(undefined, { count: Math.max((prevGetCount?.count ?? 0) - 1, 0) });
            }

            return { prevGetItem, prevGetAll, prevGetCount, decrementedCount };
        },

        onError: (_err, { productPriceId }, ctx) => {
            if (!ctx) return;
            utils.cart.getItem.setData({ productPriceId }, ctx.prevGetItem);
            if (ctx.prevGetAll) utils.cart.getAll.setData(undefined, ctx.prevGetAll);
            if (ctx.decrementedCount) {
                utils.cart.getCount.setData(undefined, ctx.prevGetCount ?? { count: 0 });
            }
        },

        onSettled: async (_data, _err, { productPriceId }) => {
            await Promise.all([
                utils.cart.getItem.invalidate({ productPriceId }),
                utils.cart.getAll.invalidate(),
                utils.cart.getCount.invalidate(),
            ]);
        },
    });
};

export const useToggleCartPostponed = () => {
    const utils = api.useUtils();

    return api.cart.setPostponed.useMutation({
        onMutate: async variables => {
            await utils.cart.getAll.cancel();

            const prevGetAll = utils.cart.getAll.getData();

            if (prevGetAll) {
                utils.cart.getAll.setData(undefined, {
                    ...prevGetAll,
                    items: prevGetAll.items.map(i =>
                        i.productPriceId === variables.productPriceId ? { ...i, postponed: variables.postponed } : i,
                    ),
                });
            }

            return { prevGetAll };
        },

        onError: (_err, _vars, ctx) => {
            if (ctx?.prevGetAll) utils.cart.getAll.setData(undefined, ctx.prevGetAll);
        },

        onSettled: async () => {
            await utils.cart.getAll.invalidate();
        },
    });
};
